<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { onClickOutside } from '@vueuse/core'
import {
  BellIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  DevicePhoneMobileIcon,
  GlobeAltIcon
} from '@heroicons/vue/24/outline'
import { useTheme } from '@/composables/useTheme'
import { usePwaInstall } from '@/composables/usePwaInstall'
import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/app'
import { useNotificationsStore } from '@/stores/notifications'

// Props
const props = defineProps({
  isMobileMenuOpen: {
    type: Boolean,
    default: false
  },
  // Se oculta al scrollear hacia abajo (mobile/tablet); reaparece al subir.
  scrollHidden: {
    type: Boolean,
    default: false
  }
})

// Setup
const router = useRouter()
const authStore = useAuthStore()
const appStore = useAppStore()
const notificationsStore = useNotificationsStore()
const { isDark, toggleTheme } = useTheme()
const { canPromptNative, isIos, isStandalone, isInstalled, promptInstall } = usePwaInstall()

// Emits
const emit = defineEmits(['toggleMobileMenu'])

// Toggle mobile menu
const handleToggleMobileMenu = () => {
  emit('toggleMobileMenu')
}

// Menú del avatar
const userMenuRef = ref(null)
const userMenuDropdownRef = ref(null)
const showUserMenu = ref(false)

const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value
}

// El dropdown se teleporta al body (el header tiene overflow-hidden para el
// colapso al scrollear), así que hay que ignorarlo para no cerrarlo al clickearlo.
onClickOutside(userMenuRef, () => {
  closeUserMenu()
}, { ignore: [userMenuDropdownRef] })

const closeUserMenu = () => {
  showUserMenu.value = false
  showDownloadMenu.value = false
  showPwaHelp.value = false
}

const goToProfile = () => {
  closeUserMenu()
  router.push('/dashboard/settings/profile')
}

// ─── Descargar app ───────────────────────────────────────────────
// Las URLs se inyectan por entorno: mientras no existan, la opción se muestra
// deshabilitada con "Próximamente" en vez de mentir con un link roto.
const ANDROID_APK_URL = import.meta.env.VITE_ANDROID_APK_URL || ''
const IOS_APP_URL = import.meta.env.VITE_IOS_APP_URL || ''

const showDownloadMenu = ref(false)
const showPwaHelp = ref(false)

const pwaAlreadyInstalled = computed(() => isStandalone.value || isInstalled.value)

const pwaHelpMessage = computed(() =>
  isIos
    ? 'Tocá el botón Compartir (cuadro con flecha hacia arriba) y luego elegí "Agregar a inicio".'
    : 'Abrí el menú de tu navegador (⋮) y elegí "Instalar app" o "Agregar a pantalla de inicio".'
)

const handleDownloadAndroid = () => {
  if (!ANDROID_APK_URL) return
  closeUserMenu()
  window.open(ANDROID_APK_URL, '_blank', 'noopener')
}

const handleDownloadIos = () => {
  if (!IOS_APP_URL) return
  closeUserMenu()
  window.open(IOS_APP_URL, '_blank', 'noopener')
}

// Android/Chromium expone el prompt nativo; iOS y el resto necesitan instrucciones.
const handleInstallPwa = async () => {
  if (canPromptNative.value) {
    await promptInstall()
    closeUserMenu()
    return
  }
  showPwaHelp.value = true
}

const goToNotifications = () => {
  router.push('/dashboard/notifications')
}

const handleLogout = async () => {
  closeUserMenu()
  try {
    await authStore.logout()
    appStore.clearErrors()
    router.push('/')
  } catch (error) {
    console.error('Error en logout:', error)
    appStore.clearErrors()
    router.push('/')
  }
}
</script>

<template>
  <!-- Dashboard Header (simplified) -->
  <header
    :class="[
      'header sticky top-0 bg-light-background dark:bg-dark-background shadow-sm dark:shadow-xl dark:border-b dark:border-dark-border z-50 overflow-hidden transition-all duration-300',
      // Debajo de lg colapsa con el scroll/drawer; en desktop siempre queda fijo visible
      (isMobileMenuOpen || scrollHidden)
        ? 'max-h-0 opacity-0 -translate-y-full pointer-events-none lg:max-h-20 lg:opacity-100 lg:translate-y-0 lg:pointer-events-auto'
        : 'max-h-20 opacity-100 translate-y-0'
    ]"
  >
    <div class="flex items-center justify-between lg:justify-end px-4 py-3">
      <!-- Mobile menu button: en desktop el sidebar ya está siempre visible -->
      <button
        @click="handleToggleMobileMenu"
        class="lg:hidden p-2 rounded-lg text-light-text-primary dark:text-dark-text-primary hover:bg-light-border dark:hover:bg-dark-border transition duration-300"
      >
        <span class="text-xl">☰</span>
      </button>

      <!-- User info and theme toggle -->
      <div class="flex items-center space-x-3">
        <!-- Theme Toggle: solo mobile, en desktop ya está en el footer del sidebar -->
        <div class="lg:hidden flex items-center">
          <button
            @click="toggleTheme"
            :title="isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'"
            class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-accent-dynamic focus:ring-offset-2 focus:ring-offset-light-background dark:focus:ring-offset-dark-background"
            :class="[
              isDark 
                ? 'bg-light-accent dark:bg-dark-accent' 
                : 'bg-light-border dark:bg-dark-border'
            ]"
          >
            <span
              class="h-4 w-4 transform rounded-full bg-light-background dark:bg-dark-background transition-transform duration-300 shadow-lg ring-0 flex items-center justify-center"
              :class="[
                isDark ? 'translate-x-6' : 'translate-x-1'
              ]"
            >
              <!-- Icon inside the toggle -->
              <svg 
                v-if="!isDark"
                :class="[
                  'w-3 h-3 transition-all duration-300',
                  'text-light-text-secondary dark:text-dark-text-secondary'
                ]"
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <!-- Moon icon -->
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
              <svg 
                v-else
                :class="[
                  'w-3 h-3 transition-all duration-300',
                  'accent-text'
                ]"
                fill="currentColor" 
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <!-- Sun icon - more distinctive -->
                <circle cx="12" cy="12" r="5" fill="currentColor"/>
                <line x1="12" y1="1" x2="12" y2="3" stroke="currentColor" stroke-width="2"/>
                <line x1="12" y1="21" x2="12" y2="23" stroke="currentColor" stroke-width="2"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" stroke="currentColor" stroke-width="2"/>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" stroke="currentColor" stroke-width="2"/>
                <line x1="1" y1="12" x2="3" y2="12" stroke="currentColor" stroke-width="2"/>
                <line x1="21" y1="12" x2="23" y2="12" stroke="currentColor" stroke-width="2"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" stroke="currentColor" stroke-width="2"/>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" stroke="currentColor" stroke-width="2"/>
              </svg>
            </span>
          </button>
        </div>

        <!-- Notificaciones: badge estilo Facebook, sin marca visible si no hay no leídas -->
        <button
          type="button"
          @click="goToNotifications"
          title="Notificaciones"
          aria-label="Notificaciones"
          class="relative p-2 rounded-full text-light-text-primary dark:text-dark-text-primary hover:bg-light-border dark:hover:bg-dark-border transition-all duration-300"
        >
          <BellIcon class="w-5 h-5" />
          <span
            v-if="notificationsStore.unreadCount > 0"
            class="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none"
          >{{ notificationsStore.unreadCount > 99 ? '99+' : notificationsStore.unreadCount }}</span>
        </button>

        <!-- User Avatar + menú -->
        <div ref="userMenuRef" class="relative">
          <button
            type="button"
            @click="toggleUserMenu"
            title="Abrir menú de usuario"
            aria-label="Abrir menú de usuario"
            aria-haspopup="menu"
            :aria-expanded="showUserMenu"
            :class="[
              'w-8 h-8 rounded-full flex items-center justify-center overflow-hidden transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-light-accent/40 dark:focus:ring-dark-accent/40',
              authStore.avatarUrl ? 'bg-transparent' : 'bg-light-accent dark:bg-dark-accent'
            ]"
          >
            <img
              v-if="authStore.avatarUrl"
              :src="authStore.avatarUrl"
              :alt="authStore.userName"
              class="h-full w-full rounded-full object-cover"
            />
            <!-- Mostrar iniciales como fallback -->
            <span
              v-else
              class="text-sm font-semibold text-light-cta-text dark:text-dark-cta-text"
            >
              {{ authStore.userName.charAt(0).toUpperCase() }}
            </span>
          </button>
        </div>

        <!-- Dropdown de usuario (teleportado al body: el header usa overflow-hidden para colapsar al scrollear) -->
        <Teleport to="body">
          <Transition
            enter-active-class="transition ease-out duration-150"
            enter-from-class="opacity-0 scale-95 -translate-y-1"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition ease-in duration-100"
            leave-from-class="opacity-100 scale-100 translate-y-0"
            leave-to-class="opacity-0 scale-95 -translate-y-1"
          >
            <div
              v-if="showUserMenu"
              ref="userMenuDropdownRef"
              role="menu"
              class="fixed top-14 right-4 w-60 origin-top-right z-[70] bg-light-background dark:bg-dark-background-modal border border-light-border dark:border-dark-border rounded-xl shadow-xl overflow-hidden py-1"
            >
              <button
                type="button"
                role="menuitem"
                @click="goToProfile"
                class="w-full text-left px-3 py-2.5 text-sm flex items-center gap-2.5 text-light-text-primary dark:text-dark-text-primary hover:bg-light-accent/10 dark:hover:bg-dark-accent/10 transition-colors duration-150"
              >
                <UserIcon class="w-5 h-5 flex-shrink-0 text-light-text-secondary dark:text-dark-text-secondary" />
                Mi perfil
              </button>

              <div class="my-1 border-t border-light-border dark:border-dark-border"></div>

              <button
                type="button"
                role="menuitem"
                :aria-expanded="showDownloadMenu"
                @click="showDownloadMenu = !showDownloadMenu"
                class="w-full text-left px-3 py-2.5 text-sm flex items-center gap-2.5 text-light-text-primary dark:text-dark-text-primary hover:bg-light-accent/10 dark:hover:bg-dark-accent/10 transition-colors duration-150"
              >
                <ArrowDownTrayIcon class="w-5 h-5 flex-shrink-0 text-light-text-secondary dark:text-dark-text-secondary" />
                <span class="flex-1">Descargar</span>
                <ChevronDownIcon
                  :class="['w-4 h-4 flex-shrink-0 text-light-text-secondary dark:text-dark-text-secondary transition-transform duration-200', showDownloadMenu ? 'rotate-180' : '']"
                />
              </button>

              <div v-if="showDownloadMenu" role="group" class="bg-light-border/20 dark:bg-black/20">
                <button
                  type="button"
                  role="menuitem"
                  :disabled="!ANDROID_APK_URL"
                  @click="handleDownloadAndroid"
                  class="w-full text-left pl-10 pr-3 py-2.5 text-sm flex items-center gap-2.5 text-light-text-primary dark:text-dark-text-primary transition-colors duration-150 enabled:hover:bg-light-accent/10 dark:enabled:hover:bg-dark-accent/10 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span class="flex-1">Android</span>
                  <span v-if="!ANDROID_APK_URL" class="text-[10px] uppercase tracking-wide text-light-text-secondary dark:text-dark-text-secondary">Pronto</span>
                </button>

                <button
                  type="button"
                  role="menuitem"
                  :disabled="!IOS_APP_URL"
                  @click="handleDownloadIos"
                  class="w-full text-left pl-10 pr-3 py-2.5 text-sm flex items-center gap-2.5 text-light-text-primary dark:text-dark-text-primary transition-colors duration-150 enabled:hover:bg-light-accent/10 dark:enabled:hover:bg-dark-accent/10 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span class="flex-1">iPhone</span>
                  <span v-if="!IOS_APP_URL" class="text-[10px] uppercase tracking-wide text-light-text-secondary dark:text-dark-text-secondary">Pronto</span>
                </button>

                <button
                  type="button"
                  role="menuitem"
                  :disabled="pwaAlreadyInstalled"
                  @click="handleInstallPwa"
                  class="w-full text-left pl-10 pr-3 py-2.5 text-sm flex items-center gap-2.5 text-light-text-primary dark:text-dark-text-primary transition-colors duration-150 enabled:hover:bg-light-accent/10 dark:enabled:hover:bg-dark-accent/10 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <GlobeAltIcon class="w-4 h-4 flex-shrink-0 text-light-text-secondary dark:text-dark-text-secondary" />
                  <span class="flex-1">PWA</span>
                  <span v-if="pwaAlreadyInstalled" class="text-[10px] uppercase tracking-wide text-light-text-secondary dark:text-dark-text-secondary">Instalada</span>
                </button>

                <p
                  v-if="showPwaHelp"
                  class="px-3 pb-3 pt-1 text-xs leading-relaxed text-light-text-secondary dark:text-dark-text-secondary"
                >
                  <DevicePhoneMobileIcon class="inline w-4 h-4 mr-1 -mt-0.5" />{{ pwaHelpMessage }}
                </p>
              </div>

              <div class="my-1 border-t border-light-border dark:border-dark-border"></div>
              <button
                type="button"
                role="menuitem"
                @click="handleLogout"
                class="w-full text-left px-3 py-2.5 text-sm flex items-center gap-2.5 text-red-600 dark:text-red-400 hover:bg-red-500/10 transition-colors duration-150"
              >
                <ArrowRightOnRectangleIcon class="w-5 h-5 flex-shrink-0" />
                Cerrar sesión
              </button>
            </div>
          </Transition>
        </Teleport>
      </div>
    </div>
  </header>
</template>
